/**
 * 모듈 싱글턴 외부 스토어 — `{ get, set, subscribe, useStore }`.
 *
 * - `set(partial | fn, replace = false)`: `fn(state)` 의 결과나 `partial` 이 지금 상태와
 *   `Object.is` 로 같으면 아무것도 안 하고 알리지도 않는다. 상태와 새 값이 둘 다 평범한 객체면
 *   얕게 합치고(`replace` 면 통째 교체), 그 밖(원시값·배열·Set)은 교체한다. 바뀌면 동기로 알린다.
 * - `useStore(selector, isEqual)`: 선택값을 컴포넌트마다 캐시한다 — 상태가 바뀌어도 선택값이
 *   `isEqual` 로 같으면 이전 참조를 돌려줘 그 컴포넌트는 다시 그려지지 않는다.
 * - 빠른 값과 느린 값은 한 스토어에 두지 말고 스토어를 둘로 가른다(perf_budget.md 「두 층」).
 */
import { useRef, useSyncExternalStore } from 'react';

const identity = (s) => s;

const isPlainObject = (v) => {
  if (v === null || typeof v !== 'object') return false;
  const proto = Object.getPrototypeOf(v);
  return proto === Object.prototype || proto === null;
};

/**
 * 셀렉터 결과 캐시 한 칸을 읽는다. `box.current` 가 `{ state, selector, value }` 를 든다.
 * 같은 상태·같은 셀렉터면 캐시를, 새 값이 이전과 `isEqual` 이면 이전 참조를 돌려준다.
 */
export function readSelection(box, state, selector, isEqual) {
  const c = box.current;
  if (c && c.state === state && c.selector === selector) return c.value;
  const next = selector(state);
  const value = c && isEqual(c.value, next) ? c.value : next;
  box.current = { state, selector, value };
  return value;
}

export function createStore(initial) {
  let state = initial;
  const listeners = new Set();

  const get = () => state;

  const subscribe = (fn) => {
    listeners.add(fn);
    return () => listeners.delete(fn);
  };

  const set = (partial, replace = false) => {
    const next = typeof partial === 'function' ? partial(state) : partial;
    if (Object.is(next, state)) return;
    state = !replace && isPlainObject(state) && isPlainObject(next) ? { ...state, ...next } : next;
    listeners.forEach((fn) => fn());
  };

  const useStore = (selector = identity, isEqual = Object.is) => {
    const box = useRef(null);
    const getSnapshot = () => readSelection(box, state, selector, isEqual);
    return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
  };

  return { get, set, subscribe, useStore };
}

export default createStore;
